import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';

import { cn } from 'epoch-ui/lib/utils';

enum NPSCategory {
  DETRACTORS = 'detractors',
  PASSIVES = 'passives',
  PROMOTERS = 'promoters'
}

interface NPSCategoryData {
  value: number;
  percentage: number;
}

interface NPSBreakdownProps {
  data: {
    [NPSCategory.DETRACTORS]: NPSCategoryData;
    [NPSCategory.PASSIVES]: NPSCategoryData;
    [NPSCategory.PROMOTERS]: NPSCategoryData;
  };
}

interface NPSLabelProps {
  x?: number;
  y?: number;
  type: NPSCategory;
  data: NPSBreakdownProps['data'];
}

const NPSLabel: React.FC<NPSLabelProps> = ({ x = 0, y = 0, type, data }) => {
  const { label, color, percentage, count } = useMemo(() => {
    const config = {
      [NPSCategory.DETRACTORS]: {
        label: 'Detractors',
        color: 'tw-text-negative-foreground',
        percentage: data[NPSCategory.DETRACTORS].percentage,
        count: data[NPSCategory.DETRACTORS].value
      },
      [NPSCategory.PASSIVES]: {
        label: 'Passives',
        color: 'tw-text-yellow-warning',
        percentage: data[NPSCategory.PASSIVES].percentage,
        count: data[NPSCategory.PASSIVES].value
      },
      [NPSCategory.PROMOTERS]: {
        label: 'Promoters',
        color: 'tw-text-positive-foreground',
        percentage: data[NPSCategory.PROMOTERS].percentage,
        count: data[NPSCategory.PROMOTERS].value
      }
    };

    return config[type] || config[NPSCategory.PROMOTERS];
  }, [data, type]);

  return (
    <g>
      <text
        x={x + 12}
        y={y + 20}
        textAnchor="start"
        fill="currentColor"
        className={cn('tw-text-xs tw-leading-4', color)}
      >
        <tspan>{label}</tspan>
      </text>
      <text
        x={x + 12}
        y={y + 44}
        textAnchor="start"
        fill="currentColor"
        className={cn('tw-text-lg tw-font-medium tw-leading-6', color)}
      >
        <tspan>
          {Math.round(percentage)}% ({count})
        </tspan>
      </text>
    </g>
  );
};

const CHART_DATA = [
  {
    name: 'NPS',
    Promoters: 65,
    gap1: 5,
    Passives: 15,
    gap2: 5,
    Detractors: 15
  }
];

const NPSBreakdown: React.FC<NPSBreakdownProps> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={60}>
      <BarChart
        data={CHART_DATA}
        layout="vertical"
        margin={{ top: -3, right: 0, bottom: -3, left: 0 }}
        barSize={60}
      >
        <XAxis type="number" domain={[0, 100]} hide />
        <YAxis type="category" dataKey="name" hide />
        <Bar
          dataKey="Promoters"
          className={cn('tw-fill-negative-red-light')}
          stackId="stack"
          radius={4}
          label={<NPSLabel type={NPSCategory.DETRACTORS} data={data} />}
        />
        <Bar dataKey="gap1" fill="transparent" stackId="stack" />
        <Bar
          dataKey="Passives"
          className={cn('tw-fill-yellow-light')}
          stackId="stack"
          radius={4}
          label={<NPSLabel type={NPSCategory.PASSIVES} data={data} />}
        />
        <Bar dataKey="gap2" fill="transparent" stackId="stack" />
        <Bar
          dataKey="Detractors"
          className={cn('tw-fill-positive-green-light')}
          stackId="stack"
          radius={4}
          label={<NPSLabel type={NPSCategory.PROMOTERS} data={data} />}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default NPSBreakdown;
